import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiBell, FiInfo, FiCheckCircle } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Input } from '@components/Input';
import { RangeSlider } from '@components/RangeSlider';
import { Callout, calloutVariants } from '@components/Callout';
import { SettingToggleCard } from '@components/SettingToggleCard';
import { addNotification, notificationStates } from '@components/Notification';
import { useAlertConfig } from '@/hooks/useAlertConfig';
import companyService from '@/services/company';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';

import styles from './AlertsNotificationsSection.module.scss';

const THRESHOLD_TICKS = ['0%', '25%', '50%', '75%', '100%'];

export const AlertsNotificationsSection = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';
  const { alertConfig } = useAlertConfig();

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(50);
  const [saving, setSaving] = useState(false);

  // Load the company's saved alert config into the editable fields whenever
  // it changes (initial fetch, or a live update from another admin's save).
  useEffect(() => {
    setEnabled(alertConfig.enabled);
    setThreshold(alertConfig.productivityPercentageThreshold);
  }, [alertConfig]);

  const handleThresholdChange = (value) => {
    const clamped = Math.min(100, Math.max(0, value));
    setThreshold(Number.isNaN(clamped) ? 0 : clamped);
  };

  const handleSave = async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Access denied: Only administrators can update alert settings',
        level: notificationStates.error,
      });
      return;
    }

    setSaving(true);
    try {
      const result = await companyService.updateAlertConfig({
        companyId: userProfile.activeCompany.companyId,
        enabled,
        productivityPercentageThreshold: threshold,
        // Not editable on this page - carry the last known value through so
        // saving here never clobbers it.
        lateLoginAlertMinutes: alertConfig.lateLoginAlertMinutes,
        updatedBy: userProfile.id,
      });
      if (!result.success) throw new Error(result.error);

      addNotification({
        message: 'Alert settings saved',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error saving alert config:', error);
      addNotification({
        message: 'Failed to save alert settings',
        level: notificationStates.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiBell size={20} color="var(--indigo-600)" />}
        title="Alerts & Notifications"
        description="Configure how alerts work across your company."
      />

      <SettingToggleCard
        title="Enable Alerts for Company"
        description="Turn alerts on or off for all employees in the company."
        checked={enabled}
        onChange={setEnabled}
        statusLabel={enabled ? 'Enabled' : 'Disabled'}
      >
        <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
          When enabled, alerts will be triggered based on the productivity
          threshold set below.
        </Callout>
      </SettingToggleCard>

      <div className={styles.sectionHeading}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Productivity Alert Threshold
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-600"
        >
          Set the minimum productivity percentage. Alerts will be triggered when
          an employee's productivity falls below this value.
        </Text>
      </div>

      <div className={styles.thresholdBlock}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-800"
        >
          Threshold
        </Text>
        <div className={styles.thresholdRow}>
          <div className={styles.thresholdInputWrap}>
            <Input
              id="productivityThreshold"
              type="number"
              min={0}
              max={100}
              value={threshold}
              onChange={(e) => handleThresholdChange(Number(e.target.value))}
              rightBlock={
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                  color="gray-500"
                >
                  %
                </Text>
              }
            />
          </div>
          <RangeSlider
            min={0}
            max={100}
            value={threshold}
            onChange={handleThresholdChange}
            tickLabels={THRESHOLD_TICKS}
            className={styles.slider}
          />
        </div>
      </div>

      <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
        Employees with productivity below {threshold}% will trigger alerts.
      </Callout>

      <div className={styles.saveRow}>
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          icon={<FiCheckCircle size={16} />}
          onClick={handleSave}
          loading={saving}
        />
      </div>
    </>
  );
};

AlertsNotificationsSection.displayName = 'AlertsNotificationsSection';
